<script setup>
// Central de avisos: falhas operacionais abertas, críticas primeiro. A lista é
// a do store (a sidebar mantém fresca: cable + refetch de 45 s); abrir a página
// rebusca na hora.
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import PageLayout from 'dashboard/components-next/layout/PageLayout.vue';
import PageHeader from 'dashboard/components-next/layout/PageHeader.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import OpsNoticeItem from './OpsNoticeItem.vue';

const { t } = useI18n();
const store = useStore();

const groups = useMapGetter('opsNotices/getGroupedBySeverity');
const uiFlags = useMapGetter('opsNotices/getUIFlags');
const truncation = useMapGetter('opsNotices/getTruncation');

const GROUP_TONES = {
  critical: 'bg-n-ruby-3 text-n-ruby-11',
  warning: 'bg-n-amber-3 text-n-amber-11',
  info: 'bg-n-blue-3 text-n-blue-11',
};

const isFirstLoad = computed(
  () => !uiFlags.value.hasLoaded && !uiFlags.value.hasError
);
const isEmpty = computed(
  () => uiFlags.value.hasLoaded && groups.value.length === 0
);

const refetch = () => store.dispatch('opsNotices/fetch');

onMounted(refetch);
</script>

<template>
  <PageLayout>
    <template #header>
      <PageHeader
        icon="i-lucide-siren"
        accent="amber"
        :title="t('OPS_NOTICES.HEADER')"
        :subtitle="t('OPS_NOTICES.DESCRIPTION')"
      />
    </template>

    <div class="flex flex-col gap-6">
      <div
        v-if="uiFlags.hasError"
        class="flex items-center gap-3 px-4 py-3 text-sm rounded-xl bg-n-ruby-3 text-n-ruby-11"
        data-test-id="ops-notices-error"
      >
        <span class="flex-shrink-0 size-4 i-lucide-cloud-off" />
        <span class="flex-1">{{ t('OPS_NOTICES.LOAD_ERROR') }}</span>
        <Button
          :label="t('OPS_NOTICES.RETRY')"
          variant="ghost"
          color="ruby"
          size="sm"
          :is-loading="uiFlags.isFetching"
          @click="refetch"
        />
      </div>

      <p
        v-if="truncation"
        class="flex items-center gap-2 px-4 py-3 m-0 text-sm rounded-xl bg-n-amber-3 text-n-amber-11"
        data-test-id="ops-notices-truncated"
      >
        <span class="flex-shrink-0 size-4 i-lucide-list-filter" />
        {{ t('OPS_NOTICES.TRUNCATED', truncation) }}
      </p>

      <div
        v-if="isFirstLoad"
        class="flex items-center gap-2 text-sm text-n-slate-11"
      >
        <Spinner class="size-4" />
        {{ t('OPS_NOTICES.LOADING') }}
      </div>

      <p
        v-else-if="isEmpty"
        class="flex items-center gap-2 m-0 text-sm text-n-slate-11"
        data-test-id="ops-notices-empty"
      >
        <span class="size-4 i-lucide-circle-check text-n-teal-11" />
        {{ t('OPS_NOTICES.EMPTY') }}
      </p>

      <section
        v-for="group in groups"
        :key="group.severity"
        class="flex flex-col gap-3"
        :data-test-id="`ops-notices-group-${group.severity}`"
      >
        <h2 class="flex items-center gap-2 text-sm font-medium text-n-slate-12">
          {{ t(`OPS_NOTICES.SEVERITY.${group.severity}`) }}
          <span
            class="px-1.5 py-0.5 rounded-md text-xs leading-none tabular-nums"
            :class="GROUP_TONES[group.severity]"
          >
            {{ group.total }}
          </span>
        </h2>
        <ul class="flex flex-col gap-2 m-0 list-none reset-base">
          <OpsNoticeItem
            v-for="notice in group.notices"
            :key="notice.id"
            :notice="notice"
          />
        </ul>
      </section>
    </div>
  </PageLayout>
</template>
